import { gql, useQuery } from '@apollo/client';
import React from 'react';
import Button from '../components/Button';
import EmptyState from '../components/EmptyState';
import RightPanel from '../components/RightPanel';
import type {
  TopicTemplatesPanelQuery,
  TopicTemplatesPanelQueryVariables,
} from '../graphql/generated/graphqlTypes';
import TemplateRow from '../templates/TemplateRow';
import useSlug from '../utils/router/useSlug';
import templatePath, { savePreviousRoute } from '../utils/url/templatesPath';
import styles from './TopicTemplatesPanel.module.scss';

const TOPIC_TEMPLATES_PANEL_QUERY = gql`
  query topicTemplatesPanel($id: ID!) {
    topic(id: $id) {
      id
      templates(includeDescendants: true) {
        id
        ...TemplateRow_template
      }
    }
  }
  ${TemplateRow.fragments.template}
`;

interface TopicTemplatesPanelProps {
  close: () => void;
}

const TopicTemplatesPanel = ({ close }: TopicTemplatesPanelProps) => {
  const [topicId] = useSlug('topicSlug');

  const { data, loading } = useQuery<
    TopicTemplatesPanelQuery,
    TopicTemplatesPanelQueryVariables
  >(TOPIC_TEMPLATES_PANEL_QUERY, {
    variables: { id: topicId },
  });

  const templates = data?.topic?.templates ?? [];

  return (
    <RightPanel header="Templates" close={close}>
      <>
        {!loading && templates.length > 0 && (
          <div className={styles.templates}>
            {templates.map((template) => (
              <TemplateRow
                key={template.id}
                currentTopicId={topicId}
                template={template}
                link="detail"
                showHoverCard
              />
            ))}
          </div>
        )}
        {!loading && templates.length === 0 && (
          <div className={styles.emptyContainer}>
            <EmptyState
              title="No templates yet…"
              imageName="posts"
              message="Templates will appear after a template has been created under this topic."
              action={
                <Button
                  size="large"
                  block
                  variant="secondary"
                  to={templatePath()}
                  onClick={() => savePreviousRoute()}
                >
                  Manage templates
                </Button>
              }
              vertical
            />
          </div>
        )}
      </>
    </RightPanel>
  );
};

export default TopicTemplatesPanel;
